import React from 'react'
import {Draggable} from 'react-beautiful-dnd'
import CountDownTimer, {
  CountDownTimerSettings,
} from 'Event/Dashboard/components/CountDownTimer'
import {CountDownTimerContainer} from 'Event/Dashboard/components/CountDownTimer/EditableCountDownTimer'
import {useEditMode} from 'Event/EditModeProvider'
import CountDownTimerConfig from 'Event/Dashboard/components/CountDownTimer/CountDownTimerConfig'
import {Editable} from 'Event/Dashboard/editor/views/EditComponent'
import {DraggableOverlay} from 'lib/ui/drag-and-drop'
import {useToggle} from 'lib/toggle'
import {
  createDuplicateCountDownTimer,
  duplicateCountDownTimerLabels,
} from 'Event/Dashboard/components/CountDownTimer/duplicate-countdown-timer'
import {useTownhallTemplate} from 'Event/template/Townhall'
import {useSaveTemplate} from 'Event/TemplateUpdateProvider'
import {useAddTranslation, useTranslationLanguage} from 'Event/LanguageProvider'

type CountDownTimerProps = {
  id: string
  countDownTimer: CountDownTimerSettings
  index: number
  onRender?: () => void
}

export default React.memo(
  (props: CountDownTimerProps & {sectionId: string}) => {
    const isEditMode = useEditMode()

    const countDownTimer = (
      <CountDownTimer
        {...props.countDownTimer}
        id={props.id}
        onRender={props.onRender}
      />
    )

    if (!isEditMode) {
      return <CountDownTimerContainer>{countDownTimer}</CountDownTimerContainer>
    }

    return (
      <EditableCountDownTimer {...props}>
        {countDownTimer}
      </EditableCountDownTimer>
    )
  },
)

function EditableCountDownTimer(
  props: CountDownTimerProps & {
    sectionId: string
    children: React.ReactElement
  },
) {
  const {flag: configVisible, toggle: toggleConfig} = useToggle()
  const duplicate = useDuplicateCountDownTimer(props.sectionId)

  return (
    <>
      <CountDownTimerConfig
        showing={configVisible}
        onClose={toggleConfig}
        id={props.id}
        keyPath={`dashboardSections.${props.sectionId}.items.${props.id}`}
        countDownTimer={props.countDownTimer}
      />
      <Draggable draggableId={props.id} index={props.index}>
        {(provided) => (
          <CountDownTimerContainer
            ref={provided.innerRef}
            draggableProps={provided.draggableProps}
          >
            <DraggableOverlay>
              <Editable
                onEdit={toggleConfig}
                onCopy={() => duplicate(props.id)}
                handleProps={provided.dragHandleProps}
              >
                <>{props.children}</>
              </Editable>
            </DraggableOverlay>
          </CountDownTimerContainer>
        )}
      </Draggable>
    </>
  )
}

function useDuplicateCountDownTimer(sectionId: string) {
  const {
    dashboardSections: {
      [sectionId]: {items},
    },
    localization,
  } = useTownhallTemplate()
  const save = useSaveTemplate()
  const addTranslation = useAddTranslation()
  const targetLanguage = useTranslationLanguage()

  return (sourceId: string) => {
    const source = items[sourceId] as CountDownTimerSettings
    const position = Object.keys(items).length + 1
    const translations = localization?.translations?.[targetLanguage] ?? {}

    return createDuplicateCountDownTimer(source, position).then(
      ({id, timer}) => {
        save({
          dashboardSections: {
            [sectionId]: {
              items: {
                [id]: timer,
              },
            },
          },
          localization: addTranslation(
            duplicateCountDownTimerLabels({id, source, translations}),
          ),
        })
      },
    )
  }
}
